import { useIntl } from 'react-intl'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import Pane from 'components/Common/Pane'
import Image from 'components/Common/Image'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import OfferBadge from 'components/Common/OfferBadge'
import OfferTilePricing from './Support/OfferTilePricing'
import OfferTileLoggedInButtons from './Support/OfferTileLoggedInButtons'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import GeoContext from 'contexts/geoContext'
import AspectRatio from 'components/utils/AspectRatio'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import Group from 'components/utils/Group'
import { getVillaCapacityText } from 'lib/homesAndVillas/getVillaCapacityText'
import ModalContext from 'contexts/ModalContext'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import SearchRentalAmenities from './SearchOfferTiles/SearchVillaTile/SearchVillaAmenities'
import VillasPageAmenitiesModalContent from 'components/OfferPage/Villas/VillasPageAmenitiesModalContent'
import Modal from 'LuxKit/Modal/Modal'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'
import { take } from 'lib/array/arrayUtils'

const Main = styled(Pane)`
  display: grid;
  position: relative;
  z-index: 0;

  ${mediaQueryUp.desktop} {
    grid-template-columns: 67% 1fr;
    grid-template-rows: 1fr;
  }
`

const DetailContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.desktop} {
    flex: 1;
    padding: ${rem(16)};
  }
`

const DetailSegment = styled.div`
  position: relative;
  padding: ${rem(20)};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    flex: 1;
    text-align: center;
    padding: ${rem(16)};
  }
`

const TopDetailSegment = styled.div`
  width: 100%;

  > * + * {
    margin-top: ${rem(12)};

    ${mediaQueryUp.desktop} {
      margin-top: ${rem(8)};
    }
  }
`

const OfferTitle = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  grid-row-gap: ${rem(4)};
  grid-column-gap: ${rem(16)};
  grid-auto-flow: column;

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr;
    grid-auto-flow: row;
    grid-row-gap: ${rem(8)};
    justify-items: center;
  }
`

const OfferTitleText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const BottomSegmentDetails = styled.div``
const BottomSegmentCTA = styled.div`
  display: flex;
  flex-direction: column;
`
const BottomSegmentExtra = styled.div`
  &:empty {
    display: none;
  }
`
const BottomSegment = styled.div`
  margin-top: ${rem(12)};
`

const BottomSegmentGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(8)};
  grid-template:
    "details cta"
    "extra extra" auto / 1fr ${rem(120)};

  > ${BottomSegmentDetails} {
    grid-area: details;
  }
  > ${BottomSegmentCTA} {
    grid-area: cta;
    align-self: end;
  }
  > ${BottomSegmentExtra} {
    grid-area: extra;
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    grid-template:
      "details"
      "." ${rem(16)}
      "cta"
      "extra" auto / 1fr;
  }
`

const BottomWalledGardenSection = styled.div`
  margin-top: ${rem(12)};
`

const UrgencyLabelsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / 1;

  ${mediaQueryUp.desktop} {
    justify-content: center;
  }

  > * + * {
    margin-inline-start: ${rem(4)};

    ${mediaQueryUp.desktop} {
      margin-inline-start: ${rem(12)};
    }
  }
`

const MainImageSegment = styled.div`
  display: grid;
  gap: ${rem(4)};
  position: relative;

  ${mediaQueryUp.desktop} {
    &.additional {
      grid-template-rows: 1fr ${rem(120)};
    }
  }
`

const AdditionalImages = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    gap: ${rem(4)};
  }
`

const OfferOverlayContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(12)};
  inset-inline-start: ${rem(12)};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const StyledOfferBadge = styled(OfferBadge)`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
`

const USDesktopRating = styled(OfferRating)`
  justify-content: center;
`

const StyledOfferTilePricing = styled(OfferTilePricing)`
  align-items: flex-start;

  ${mediaQueryUp.desktop} {
    align-items: center;
  }
`

const TileUrgencyLabels = styled(OfferLabels)`
  ${mediaQueryUp.desktop} {
    justify-content: center;
  }
`

const OfferBadgeTagWrapper = UrgencyLabelsWrapper

interface Props {
  offer: App.VillaOffer | App.VillaOfferSummary
  eagerLoadFirstImage?: boolean
  offerUrl: string
}

const imageParams: ImageParams = {
  aspectRatio: '16:9',
  desktopWidth: '67vw',
  largeDesktopWidth: '750px',
  quality: 'good',
}

const DesktopOffer = styled.div`
  display: none;
  ${mediaQueryUp.desktop} {
    display: block;
  }
`

const MobileOffer = styled.div`
  display: block;
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

function HomesAndVillasOfferTile(props: Props) {
  const {
    offer,
    eagerLoadFirstImage,
    offerUrl,
  } = props

  const {
    property,
    name,
    lowestPricePackage,
  } = offer
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const showAdditionalImages = offer.images.length > 3

  const location = offer.locationHeading && offer.locationSubheading
    ? intl.formatMessage(
        {
          id: 'dewADRMErk7Ef8V07pi-',
          defaultMessage: '{locationHeading}, {locationSubheading}',
          description: 'Homes and villas offer tile location heading and subheading separated by comma',
        },
        { locationHeading: offer.locationHeading, locationSubheading: offer.locationSubheading },
      )
    : (offer.locationHeading || offer.locationSubheading || '')
  const capacityTitle = useMemo(() => getVillaCapacityText(intl, offer), [intl, offer])

  const pricingSection = <BottomSegmentGrid>
    <BottomSegmentDetails>
      {lowestPricePackage &&
        <StyledOfferTilePricing
          offer={offer}
          pkg={lowestPricePackage}
          offerUrl={offerUrl}
        />}
    </BottomSegmentDetails>
    <BottomSegmentCTA>
      <TextButton kind="primary">
        <DesktopOffer>{intl.formatMessage({
          id: '9BmKNlRL5mrkqGCX3JXt',
          defaultMessage: 'View Offer',
          description: 'Desktop CTA label to view villa/unique stay offer details',
        })}
        </DesktopOffer>
        <MobileOffer>{intl.formatMessage({
          id: 'l8q60OfRq-jGvDRmHMjn',
          defaultMessage: 'View',
          description: 'Mobile CTA label to view villa/unique stay offer details',
        })}
        </MobileOffer>
      </TextButton>
    </BottomSegmentCTA>
  </BottomSegmentGrid>

  const isUSRegion = currentRegionCode === 'US'

  const showModal = useContext(ModalContext)
  // villas only have 1 'room' type
  const amenities = offer.packages[0]?.roomType?.amenities
  const openAmenitiesModal = useCallback((e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    showModal(
      <Modal title={intl.formatMessage({
        id: 'UK7ssXBLJJUTwSyTaAUt',
        defaultMessage: 'Amenities',
        description: 'Title for the amenities modal in homes and villas offer tile',
      })}
      >
        <VillasPageAmenitiesModalContent amenities={amenities} />
      </Modal>,
    )
  }, [showModal, amenities, intl])

  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  return (
    <ProductPaletteProvider product={offer}>
      <Main border="light-foreground" borderRadius="S">
        <MainImageSegment className={clsx({ additional: showAdditionalImages })}>
          <AspectRatio ratio={showAdditionalImages ? '16:7' : '16:9'}>
            <ImageCarousel
              images={offer.images}
              imageParams={imageParams}
              eagerLoadFirstImage={eagerLoadFirstImage}
              onImageLoad={handleImageLoaded}
            />
          </AspectRatio>
          {showAdditionalImages && <AdditionalImages>
            {take(offer.images, 3, 1).map((image) => (
              <Image
                key={image.id}
                id={image.id}
                alt={name}
                height={120}
                width={245}
                loading="lazy"
                fit="center"
              />
            ))}
          </AdditionalImages>}

          <OfferOverlayContainer>
            <ProductTypeLabel productType={offer.productType} />
            <OfferPartnershipLabel offer={offer} />
          </OfferOverlayContainer>

          {offer.badge && <StyledOfferBadge badge={offer.badge} />}
        </MainImageSegment>
        <DetailContainer>
          <CSSBreakpoint min="desktop">
            <Group direction="horizontal" horizontalAlign="space-between">
              {!isUSRegion && isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) && (
                <OfferRating variant="medium" rating={offer.property.rating} />
              )}
              <TripGuard>
                <BookmarkButton offer={offer} />
              </TripGuard>
            </Group>
          </CSSBreakpoint>
          <DetailSegment>
            <TripGuard>
              <CSSBreakpoint max="tablet">
                <OffsetBookmarkContainer>
                  <BookmarkButton offer={offer} />
                </OffsetBookmarkContainer>
              </CSSBreakpoint>
            </TripGuard>
            <TopDetailSegment>
              <OfferTitle>
                <TileUrgencyLabels offer={offer} />
                <OfferTitleText>
                  <BodyText variant="large">{location}</BodyText>
                  <Heading variant="heading5">{property.name}</Heading>
                </OfferTitleText>
              </OfferTitle>
              {isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) && (
                <>
                  <CSSBreakpoint max="tablet">
                    <OfferRating
                      inlineLabel
                      variant="xs"
                      rating={offer.property.rating}
                    />
                  </CSSBreakpoint>
                  {isUSRegion && (
                    <CSSBreakpoint min="desktop">
                      <USDesktopRating
                        inlineLabel
                        variant="xs"
                        rating={offer.property.rating}
                      />
                    </CSSBreakpoint>
                  )}
                </>
              )}
              <Group direction="vertical" desktopHorizontalAlign="center" gap={8} desktopGap={12}>
                {capacityTitle && <BodyTextBlock variant="medium">{capacityTitle}</BodyTextBlock>}
                {!!amenities?.length && <Group direction="vertical" gap={16}>
                  <SearchRentalAmenities amenities={take(amenities, 4)} />
                  <TextLink weight="bold" underline size="medium" onClick={openAmenitiesModal}>
                    {intl.formatMessage({
                      id: '4sQej0U04q3Gc9LEk_tb',
                      defaultMessage: 'View all {count} amenities',
                      description: 'Link text to open modal showing all amenities for a villa offer',
                    }, { count: amenities.length })}
                  </TextLink>
                </Group>}
                {!!offer.badge && <OfferBadgeTagWrapper>
                  <OfferBadgeLabel badge={offer.badge} />
                </OfferBadgeTagWrapper>}
              </Group>
            </TopDetailSegment>

            <BottomSegment>
              {!offer.walledGarden && pricingSection}
              {offer.walledGarden && <OfferTileLoggedInButtons
                signUpText={intl.formatMessage({
                  id: 'lKbV7gGZCG5xQmFkC1Yn',
                  defaultMessage: 'Sign up for free',
                  description: 'Sign up button text for walled garden villa offer tile',
                })}
                discountPercentage={lowestPricePackage?.defaultPricing?.discountPercent ?? 0}
                align="none"
                fullWidth
              >
                <BottomWalledGardenSection>
                  {pricingSection}
                </BottomWalledGardenSection>
              </OfferTileLoggedInButtons>}
            </BottomSegment>
          </DetailSegment>
        </DetailContainer>
      </Main>
    </ProductPaletteProvider>
  )
}

export default HomesAndVillasOfferTile
